import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { t } from 'ttag';
import { RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';
import { Skeleton } from 'components';

import './SubCategories.css';

/**
 * Debounces the given function with the given delay.
 * @param {function} func - The function to debounce.
 * @param {number} timeout - The number of milliseconds to delay.
 */
function debounce(func, timeout = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
}

export default function SubCategories({
  subCategories,
  hasSubCategories,
  pathname,
  pushUrl
}) {
  /**
   * subCategoriesSlider Reference to be used to target the subCategoriesSlider element in the DOM.
   * @type {RefObject<HTMLUListElement>}
   */
  const subCategoriesSlider = useRef(null);

  const [isSliderAtStart, setIsSliderAtStart] = useState(
    /** @type {boolean} Determines if the subCategoriesSlider is at the start. */ true
  );
  const [isSliderAtEnd, setIsSliderAtEnd] = useState(
    /** @type {boolean} Determines if the subCategoriesSlider is at the end. */ true
  );

  /**
   * Scrolls the subCategoriesSlider to the left or right depending on the direction parameter passed.
   * @param {string} direction - The direction to navigate the subCategoriesSlider.
   * @type {function(string): void}
   */
  const navigateSlider = useCallback(
    direction => {
      /**
       * @type {number} minusOffset - The offset to subtract from the clientWidth value of the subCategoriesSlider.
       * */
      const minusOffset = 150;

      if (subCategoriesSlider?.current) {
        if (direction === 'next')
          subCategoriesSlider.current.scrollLeft +=
            subCategoriesSlider.current.clientWidth - minusOffset;
        if (direction === 'previous')
          subCategoriesSlider.current.scrollLeft -=
            subCategoriesSlider.current.clientWidth - minusOffset;
      }
    },
    [subCategoriesSlider]
  );

  /**
   * Checks if the subCategoriesSlider is at the start or end and sets the isSliderAtStart and isSliderAtEnd state accordingly.
   * Adds an EventListener to the subCategoriesSlider to check if the subCategoriesSlider is at the start or end, when the viewport is resized.
   * @type {React.useLayoutEffect<void>}
   */
  useLayoutEffect(() => {
    const handleResize = () => {
      if (subCategoriesSlider?.current) {
        setIsSliderAtStart(subCategoriesSlider.current.scrollLeft === 0);
        setIsSliderAtEnd(
          subCategoriesSlider.current.scrollLeft ===
            subCategoriesSlider.current.scrollWidth -
              subCategoriesSlider.current.clientWidth
        );
      }
    };

    window.addEventListener('resize', debounce(handleResize, 250));

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [subCategoriesSlider, subCategories]);

  /**
   * Adds an EventListener to the subCategoriesSlider to check if the subCategoriesSlider is at the start or end, when the subCategoriesSlider is scrolled.
   * @type {React.useEffect<void>}
   */
  useEffect(() => {
    const handleScroll = () => {
      setIsSliderAtStart(subCategoriesSlider.current.scrollLeft === 0);

      setIsSliderAtEnd(
        subCategoriesSlider.current.scrollLeft ===
          subCategoriesSlider.current.scrollWidth -
            subCategoriesSlider.current.clientWidth
      );
    };

    if (subCategoriesSlider?.current) {
      subCategoriesSlider.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (subCategoriesSlider?.current) {
        subCategoriesSlider.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [subCategoriesSlider]);

  /**
   * Resets the subCategoriesSlider to the start when the subCategories changes and there are new subCategories.
   * @type {React.useEffect<void>}
   */
  useEffect(() => {
    if (subCategoriesSlider?.current && hasSubCategories) {
      subCategoriesSlider.current.scrollLeft = 0;
    }
  }, [subCategoriesSlider, subCategories]);

  return (
    <div className="relative flex items-center">
      <button
        className={clsx(
          'absolute left-0 hidden shrink-0 cursor-pointer items-center border-0 bg-light-white-smoke p-2 transition-opacity hover:bg-whisper sm:flex',
          {
            'pointer-events-none opacity-0': isSliderAtStart
          }
        )}
        type="button"
        onClick={() => navigateSlider('previous')}
        aria-label={t`Previous`}
      >
        <RiArrowLeftSLine className={clsx('h-6 w-6')} />
      </button>
      <ul
        className="hide-scrollbar m-0 flex items-center space-x-2 overflow-x-auto scroll-smooth p-0"
        ref={subCategoriesSlider}
      >
        {subCategories
          ? subCategories.map(({ title, slug }) => (
              <li className="shrink-0" key={slug}>
                <button
                  type="button"
                  onClick={() => pushUrl(slug)}
                  className={clsx(
                    'flex cursor-pointer appearance-none items-center justify-start space-x-2 rounded-full border border-solid bg-white px-4 py-2 text-left text-sm text-black focus:outline-none focus-visible:border-black enabled:hover:bg-light-white-smoke enabled:active:bg-white-smoke',
                    {
                      'border-green': pathname === slug,
                      'border-gray': pathname !== slug
                    }
                  )}
                  disabled={pathname === slug}
                >
                  {title}
                </button>
              </li>
            ))
          : Array.from(Array(4).keys()).map(index => (
              <li className="shrink-0" key={index}>
                <Skeleton width={120} height={38} isLoading />
              </li>
            ))}
      </ul>
      <button
        className={clsx(
          'absolute right-0 hidden shrink-0 cursor-pointer items-center border-0 bg-light-white-smoke p-2 transition-opacity hover:bg-whisper sm:flex',
          {
            'pointer-events-none opacity-0': isSliderAtEnd
          }
        )}
        type="button"
        onClick={() => navigateSlider('next')}
        aria-label={t`Next`}
      >
        <RiArrowRightSLine className={clsx('h-6 w-6')} />
      </button>
    </div>
  );
}

SubCategories.propTypes = {
  /**
   * The Sub Categories to Display
   */
  subCategories: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      slug: PropTypes.string
    })
  ),
  /**
   * Determines if the Sub Category has additional Sub Categories
   */
  hasSubCategories: PropTypes.bool,
  /**
   * The URL Pathname
   */
  pathname: PropTypes.string.isRequired,
  /**
   * The Push URL Function
   */
  pushUrl: PropTypes.func.isRequired
};

SubCategories.defaultProps = {
  subCategories: undefined,
  hasSubCategories: false
};
